import axios from 'axios'
import { uniqBy } from 'lodash-es'
import { API_URL, getAuthHeader, getAllowedEndpoints } from '@/services/api.service.js'
import { axiosCacheClient } from '@/services/axios-cache-client'
import NewPlanOptions from '@/lib/NewPlanOptions'
import { duplicateActivityAnalysis } from '@/services/operating-model-api.service.js'
import { logger } from '@/utils/logger'

export const getBoards = async ({ userId, forceTokenRefresh = true }) => {
  if (!userId) {
    logger.error('userId is undefined')
    return null
  }

  const ret = await axiosCacheClient.get(`${await API_URL()}/boards`, {
    headers: {
      ...(await getAuthHeader(forceTokenRefresh))
    }
  })

  if (ret.status === 200) {
    const boards = []

    ret.data.forEach((data) => {
      if (!data.companyId || data.companyId === 'undefined' || data.companyId === '') {
        data.companyName = null
        data.companyId = null
      }

      if (!data.accessLevels) {
        data.accessLevels = []
      }

      const uniqAccessLevels = uniqBy(data.accessLevels, 'uid')
      if (uniqAccessLevels.length !== data.accessLevels.length) {
        data.accessLevels = uniqAccessLevels
      }

      boards.push(data)
    })

    return boards
  } else throw new Error(ret.statusText)
}

export const getBoard = async ({ boardId }) => {
  try {
    const ret = await axios.get(`${await API_URL()}/boards/${boardId}`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

export const getBoardMeta = async ({ boardId, key }) => {
  try {
    const ret = await axios.get(`${await API_URL()}/boards/${boardId}/meta/${key}`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

export const getBoardMapping = async ({ boardId }) => {
  try {
    const ret = await axiosCacheClient.get(`${await API_URL()}/boards/${boardId}/board-mapping`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

export const updateBoardMeta = async ({ boardId, key, data }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${boardId}/meta/${key}`,
      { data: { payload: data } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

export const updateBoard = async ({ boardId, payload }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${boardId}`,
      { data: { payload } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

/**
 * Update the access level for a user on a specific board.
 *
 * @param {Object} options - An object containing the parameters for the update.
 * @param {string} options.targetUid - The unique identifier of the target user.
 * @param {string} options.newAccessLevel - The new access level to assign to the user.
 * @param {string} options.boardId - The unique identifier of the board to update.
 */
export const updateAccessLevel = async ({ targetUid, newAccessLevel, boardId }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${boardId}/updateAccessLevel`,
      { data: { targetUid, newAccessLevel } },
      { headers: { ...(await getAuthHeader()) } }
    )
    if (ret.status === 200) {
      return ret.data
    } else {
      throw new Error(ret.statusText)
    }
  } catch (error) {
    logger.info(error)
    throw error
  }
}

export const submitBoard = async ({ boardId, payload }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${boardId}/submit`,
      { data: { payload } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) {
      return { ok: true, data: ret.data }
    }
    throw new Error(ret.statusText)
  } catch (error) {
    const message = error?.response?.data || error?.message || 'Submission failed'
    const status = error?.response?.status || null
    return { ok: false, status, message }
  }
}

export const unsubmitBoard = async ({ boardId, payload }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${boardId}/unsubmit`,
      { data: { payload } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

export const createBoard = async ({ payload }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/boards`,
      { data: { payload } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 201) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

/**
 * Create a board against a specific regional endpoint (Agentnoon staff use-case).
 * - In production, chooses the appropriate regional API subdomain by region key.
 * - In staging/preview, always uses the staging API.
 * - In localhost dev, uses localhost API.
 */
export const createBoardInRegion = async ({ payload, region }) => {
  // Resolve base URL based on environment/region
  const key = (region || '').toLowerCase()

  const map = await getAllowedEndpoints()

  let baseUrl = map.get(key)

  try {
    const ret = await axios.post(
      `${baseUrl}/boards`,
      { data: { payload } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 201) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

export const cloneInstance = async ({ payload }) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${payload.srcBoardId}/clone-instance`,
      { data: { payload } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 201) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

export const deleteBoard = async ({ boardId }) => {
  try {
    const ret = await axios.delete(`${await API_URL()}/boards/${boardId}`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      // Return number of boards rows deleted
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

/**
 * Force delete a main board and all associated scenarios/data.
 * This is intended for dev/testing purposes (e.g., Dayforce simulator).
 *
 * @param {string} boardId - The main board ID to delete
 * @returns {Promise<Object|null>} Summary of deleted rows or null on error
 */
export const deleteMainBoard = async ({ boardId }) => {
  try {
    const ret = await axios.delete(`${await API_URL()}/boards/${boardId}/force`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

/**
 * Call backend to clone a board
 *
 * @param newBoardId (Required) the new board ID
 * @param srcBoardId (Required) the target board to clone
 * @param newBoardName (Required) the name of the new board
 * @param newBoardDescription the description of the new board
 * @param boardProperties Additional board properties for the new board
 * @param filter to apply to the new board i.e { name: "department", value: "Product"}
 * @param planType one of the entry from @/lib/NewPlanOptions - If duplicating an existing plan, planType must be left empty ''
 * @returns {Promise} Return the boardId of the cloned board
 */
export const cloneBoard = async ({
  newBoardId,
  srcBoardId,
  newBoardName,
  newBoardDescription,
  planType = '',
  boardProperties,
  filter,
  mainBoardId
}) => {
  const payload = {
    newBoardId,
    srcBoardId,
    newBoardName,
    newBoardDescription,
    planType,
    boardProperties,
    filter
  }

  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${srcBoardId}/clone`,
      { data: { payload } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (mainBoardId !== srcBoardId) {
      // Only duplicate activity analysis if cloning a scenario and not a main board
      duplicateActivityAnalysis({
        boardId: mainBoardId,
        planId: srcBoardId,
        newPlanId: newBoardId
      })
    }

    if (ret.status === 201) {
      return ret.data.boardId
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (error.response?.status !== 404) throw error.response.data
    else throw new Error('Something went wrong while creating the plan')
  }
}

/**
 * Call backend to clone a board
 *
 * @param newBoardId (Required) the new board ID
 * @param srcBoardId (Required) the target board to clone
 * @param newBoardName (Required) the name of the new board
 * @param newBoardDescription the description of the new board
 * @param boardProperties Additional board properties for the new board
 * @param filters to apply to the new board i.e { name: "department", value: "Product"}
 * @param planType one of the entry from @/lib/NewPlanOptions
 * @returns {Promise} Return the boardId of the cloned board
 */
export const cloneBoardWithFilters = async ({
  newBoardId,
  srcBoardId,
  newBoardName,
  filters,
  boardProperties
}) => {
  const payload = {
    newBoardId,
    srcBoardId,
    newBoardName,
    planType: NewPlanOptions.Filter,
    boardProperties,
    filters
  }

  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${srcBoardId}/clone`,
      { data: { payload } },
      { headers: { ...(await getAuthHeader()) } }
    )
    if (ret.status === 201) {
      return ret.data.boardId
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (error.response?.status !== 404) throw error.response.data
    else throw new Error('Something went wrong while creating the plan')
  }
}

/**
 * Call backend to clone a board
 *
 * @param newBoardId (Required) the new board ID
 * @param srcBoardId (Required) the target board to clone
 * @param newBoardName (Required) the name of the new board
 * @param newBoardDescription the description of the new board
 * @param boardProperties Additional board properties for the new board
 * @param filters to apply to the new board i.e { name: "department", value: "Product"}
 * @param planType one of the entry from @/lib/NewPlanOptions
 * @returns {Promise} Return the boardId of the cloned board
 */
export const editBoard = async ({ newBoardId, srcBoardId, planType = '' }) => {
  const payload = {
    newBoardId,
    srcBoardId,
    planType
  }

  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${srcBoardId}/clone`,
      { data: { payload } },
      { headers: { ...(await getAuthHeader()) } }
    )
    if (ret.status === 201) {
      return ret.data.boardId
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (error.response?.status !== 404) throw error.response.data
    else throw new Error('Something went wrong while creating the plan')
  }
}

/**
 * Call backend to clone a board
 *
 * @param newBoardId (Required) the new board ID
 * @param srcBoardId (Required) the target board to clone
 * @param filter to apply to the new board i.e { name: "department", value: "Product"}
 * @returns {Promise} Return the boardId of the cloned board
 */
export const editBoardWithFilters = async ({ newBoardId, srcBoardId, filters }) => {
  const payload = {
    newBoardId,
    srcBoardId,
    planType: NewPlanOptions.Filter,
    filters
  }

  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${srcBoardId}/clone`,
      { data: { payload } },
      { headers: { ...(await getAuthHeader()) } }
    )
    if (ret.status === 201) {
      return ret.data.boardId
    } else throw new Error(ret.statusText)
  } catch (error) {
    if (error.response?.status !== 404) throw error.response.data
    else throw new Error('Something went wrong while creating the plan')
  }
}

/**
 * Call backend to re-sync the dest board with the src board
 * The backend will maintain the nodes that have scenarioMetaData
 *
 * @param srcBoardId (Required) The src board. Typically the main board
 * @param destBoardId (Required) the destination board. Typically a plan.
 * @returns {Promise} Return the status
 */
export const resync = async ({
  srcBoardId,
  destBoardId,
  newBoardId,
  newBoardName,
  filters,
  planType
}) => {
  const payload = {
    newBoardId,
    newBoardName,
    filters,
    planType,
    mainBoardId: srcBoardId
  }

  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${destBoardId}/resync`,
      { data: { payload } },
      {
        headers: { ...(await getAuthHeader()) }
      }
    )
    if (ret.status === 201 || ret.status === 200) {
      return ret.data
    } else {
      logger.info(ret)
      throw new Error(ret.statusText)
    }
  } catch (error) {
    logger.info(error)
    return null
  }
}

/**
 * Call backend to sync the new board with conflict resolution
 *
 * @param boardId (Required) The new board id
 * @returns {Promise} Return the status
 */
export const completeResync = async ({
  boardId,
  selectedPeopleFromOldScenario,
  selectedConflictFields,
  oldBoardId
}) => {
  const payload = {
    selectedPeopleFromOldScenario,
    selectedConflictFields,
    oldBoardId
  }

  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${boardId}/complete-resync`,
      { data: { payload } },
      {
        headers: { ...(await getAuthHeader()) }
      }
    )
    if (ret.status === 201 || ret.status === 200) {
      return ret.data
    } else {
      logger.info(ret)
      throw new Error(ret.statusText)
    }
  } catch (error) {
    logger.info(error)
    return null
  }
}

/**
 * Retrieves the board history from the server.
 *
 * @param {Object} options - The options for retrieving the board history.
 * @param {string} options.boardId - The ID of the board.
 * @param {string} [options.personId] - Optional person ID to filter by.
 * @returns {Promise<Object|null>} - A promise that resolves to the board history data or null if an error occurs.
 */
export async function getBoardHistory({ boardId, personId }) {
  try {
    const url = personId
      ? `${await API_URL()}/boards/${boardId}/history/${personId}`
      : `${await API_URL()}/boards/${boardId}/history`

    const ret = await axios.get(url, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    }

    throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

/**
 * Retrieves one paged slice of board history from the server.
 *
 * @param {Object} options - The options for retrieving the board history page.
 * @param {string} options.boardId - The ID of the board.
 * @param {string} [options.personId] - Optional person ID to filter by.
 * @param {number} [options.limit=100] - Number of visible history rows to request. The backend caps paged requests at 500.
 * @param {string} [options.cursor] - Optional id of the last visible item from the previous page.
 * @returns {Promise<Object>} - A promise that resolves to the paged history payload.
 */
export async function getBoardHistoryPage({ boardId, personId, limit = 100, cursor }) {
  try {
    const url = personId
      ? `${await API_URL()}/boards/${boardId}/history/${personId}`
      : `${await API_URL()}/boards/${boardId}/history`

    const params = { limit }
    if (cursor) params.cursor = cursor

    const ret = await axios.get(url, {
      headers: { ...(await getAuthHeader()) },
      params
    })

    if (ret.status === 200) {
      return ret.data
    }

    throw new Error(ret.statusText)
  } catch (error) {
    logger.error('Failed to fetch paged board history:', error.message)
    throw error
  }
}

/**
 * Retrieves the full detail for one grouped activity log row.
 *
 * @param {Object} options - The options for retrieving grouped history detail.
 * @param {string} options.boardId - The ID of the board.
 * @param {string} options.detailKey - The visible group key to hydrate.
 * @param {string} [options.personId] - Optional person ID used to verify the group contains that person.
 * The response still contains the full group detail.
 * @returns {Promise<Object>} - A promise that resolves to the grouped history detail payload.
 */
export async function getBoardHistoryGroupDetail({ boardId, detailKey, personId }) {
  try {
    const params = {}
    if (personId) params.personId = personId

    const ret = await axios.get(
      `${await API_URL()}/boards/${boardId}/history/groups/${encodeURIComponent(detailKey)}`,
      {
        headers: { ...(await getAuthHeader()) },
        params
      }
    )

    if (ret.status === 200) {
      return ret.data
    }

    throw new Error(ret.statusText)
  } catch (error) {
    logger.error('Failed to fetch grouped board history detail:', error.message)
    throw error
  }
}

/**
 * Export scenario activity log as CSV file
 * @param {string} boardId - The board ID
 * @param {string} personId - Optional person ID to filter by
 * @param {string} dateFrom - Optional start date (YYYY-MM-DD format)
 * @param {string} dateTo - Optional end date (YYYY-MM-DD format)
 * @returns {Promise<Blob|null>} CSV file blob or null on error
 */
export const exportActivityLogCsv = async ({ boardId, personId, dateFrom, dateTo }) => {
  try {
    const url = `${await API_URL()}/boards/${boardId}/history/export-csv`
    const params = {}
    if (personId) params.personId = personId
    if (dateFrom) params.dateFrom = dateFrom
    if (dateTo) params.dateTo = dateTo

    const response = await axios.get(url, {
      headers: { ...(await getAuthHeader()) },
      params,
      responseType: 'blob'
    })

    if (response.status === 200) {
      return response.data
    } else {
      throw new Error(response.statusText)
    }
  } catch (error) {
    logger.error('Error exporting activity log to CSV:', error.message)
    return null
  }
}

export const getBoardOwnerEmail = async ({ boardId }) => {
  try {
    const ret = await axios.get(`${await API_URL()}/boards/${boardId}/owner-email`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.info(error)
    return null
  }
}

/**
 * Kick off conflict computation for an async merge attempt.
 * @param {object} options
 * @param {string} options.boardId - Destination board id.
 * @param {string} options.srcBoardId - Source board id.
 * @param {Array} options.fields
 * @param {Array} options.filters
 * @param {boolean} options.mergeEveryone
 * @returns {Promise<{processId: string, boardId: string}>} The kicked-off attempt's id.
 */
export async function fetchMergeConflicts({ boardId, srcBoardId, fields, filters, mergeEveryone }) {
  const ret = await axios.post(
    `${await API_URL()}/boards/${boardId}/merge-conflicts`,
    {
      data: {
        srcBoardId,
        fields,
        filters,
        mergeEveryone
      }
    },
    {
      headers: { ...(await getAuthHeader()) }
    }
  )

  if (ret.status !== 202) throw new Error(ret.statusText)
  return ret.data
}

/**
 * Kick off merge attempt execution, given a processId from a prior fetchMergeConflicts()
 * call whose attempt is currently awaiting resolution.
 * @param {object} options
 * @param {string} options.boardId - Destination board id.
 * @param {string} options.srcBoardId - Source board id.
 * @param {string} options.processId - The attempt id returned by fetchMergeConflicts().
 * @param {Array} options.fields
 * @param {Array} [options.filters]
 * @param {Array} options.personIdsToMerge
 * @param {boolean} options.isAllFieldsSelected
 * @param {Array} options.conflictsResolvedToDestinationBoard
 * @param {boolean} options.resolveAllConflictingFieldsToSource
 * @returns {Promise<{processId: string, boardId: string}>} The kicked-off attempt's id.
 */
export async function mergeBoard({
  boardId,
  srcBoardId,
  processId,
  fields,
  filters,
  personIdsToMerge,
  isAllFieldsSelected,
  conflictsResolvedToDestinationBoard,
  resolveAllConflictingFieldsToSource
}) {
  const ret = await axios.post(
    `${await API_URL()}/boards/${boardId}/merge`,
    {
      data: {
        srcBoardId,
        processId,
        fields,
        filters,
        personIdsToMerge,
        isAllFieldsSelected,
        conflictsResolvedToDestinationBoard,
        resolveAllConflictingFieldsToSource
      }
    },
    {
      headers: { ...(await getAuthHeader()) }
    }
  )

  if (ret.status !== 202) throw new Error(ret.statusText)
  return ret.data
}

/**
 * Cancel an in-progress merge attempt. Only effective while the attempt is still
 * computing_conflicts or awaiting_resolution — returns null once merging has started
 * (the backend rejects with 409, which is an expected outcome here, not an error).
 * @param {object} options
 * @param {string} options.boardId - Destination board id.
 * @param {string} options.processId
 * @returns {Promise<object|null>} The updated attempt status, or null if it couldn't be cancelled.
 */
export async function cancelMergeAttempt({ boardId, processId }) {
  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${boardId}/merge/${processId}/cancel`,
      {},
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    return ret.data
  } catch (error) {
    logger.info(error)
    return null
  }
}

/**
 * Fetch a merge attempt's payload by processId, from either the source or destination
 * board's perspective — used by the scenario-list badge, which doesn't know in advance
 * which side of a merge a given row is on.
 * @param {object} options
 * @param {string} options.boardId
 * @param {string} options.processId
 * @returns {Promise<object|null>} The attempt payload, or null if not found/not involved.
 */
export async function fetchMergeAttempt({ boardId, processId }) {
  try {
    const ret = await axios.get(`${await API_URL()}/boards/${boardId}/merge-attempt/${processId}`, {
      headers: { ...(await getAuthHeader()) }
    })

    return ret.data
  } catch (error) {
    logger.info(error)
    return null
  }
}

/**
 * Fetch all approved scenarios that can be merged to main board
 * @param {string} mainBoardId - The main board ID
 * @returns {Promise<Object|null>} Object containing scenarios array or null on error
 */
export async function fetchApprovedScenarios({ mainBoardId }) {
  try {
    const ret = await axios.get(`${await API_URL()}/boards/${mainBoardId}/approved-scenarios`, {
      headers: { ...(await getAuthHeader()) }
    })

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.error('Error fetching approved scenarios:', error.message)
    return null
  }
}

/**
 * Prepare merge conflicts for merging an approved scenario to main board
 * @param {string} mainBoardId - The main board ID to merge into
 * @param {string} scenarioBoardId - The approved scenario board ID to merge from
 * @param {Array} fields - Fields to compare and merge
 * @param {boolean} mergeEveryone - Include all people from scenario if true
 * @returns {Promise<Object|null>} Merge conflict summary or null on error
 */
export async function prepareMergeToMain({ mainBoardId, scenarioBoardId, fields, mergeEveryone }) {
  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${mainBoardId}/merge-to-main/prepare`,
      {
        data: {
          scenarioBoardId,
          fields,
          mergeEveryone
        }
      },
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.error('Error preparing merge to main:', error.message)
    return null
  }
}

/**
 * Merge an approved scenario to main board
 * @param {string} mainBoardId - The main board ID to merge into
 * @param {string} scenarioBoardId - The approved scenario board ID to merge from
 * @param {Array} fields - Fields to merge
 * @param {Array} personIdsToMerge - Person IDs to merge from scenario
 * @param {boolean} isAllFieldsSelected - If true, merge all applicable fields
 * @param {Array} conflictsResolvedToDestinationBoard - Conflicts resolved to keep main board values
 * @param {boolean} resolveAllConflictingFieldsToSource - If true, scenario values win all conflicts
 * @param {boolean} includeNewPositions - If true, include newly added positions from scenario
 * @returns {Promise<Object|null>} Result object or null on error
 */
export async function mergeToMain({
  mainBoardId,
  scenarioBoardId,
  fields,
  personIdsToMerge,
  isAllFieldsSelected,
  conflictsResolvedToDestinationBoard,
  resolveAllConflictingFieldsToSource,
  includeNewPositions = true
}) {
  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${mainBoardId}/merge-to-main`,
      {
        data: {
          scenarioBoardId,
          fields,
          personIdsToMerge,
          isAllFieldsSelected,
          conflictsResolvedToDestinationBoard,
          resolveAllConflictingFieldsToSource,
          includeNewPositions
        }
      },
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.error('Error merging to main:', error.message)
    throw error
  }
}

/**
 * Fetches the preview data for scenario data refresh.
 * Returns people in the scenario that can be refreshed and available fields.
 *
 * @param {Object} options
 * @param {string} options.boardId - The scenario board ID
 * @returns {Promise<Object|null>} - { people, fields } or null on error
 */
export const getScenarioDataRefreshPreview = async ({ boardId }) => {
  try {
    const ret = await axios.get(
      `${await API_URL()}/boards/${boardId}/scenario-data-refresh/preview`,
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.error(error.message)
    return null
  }
}

/**
 * Kicks off conflict detection for a scenario data refresh: finds fields that have been
 * edited in the scenario and would be overwritten by main board values.
 *
 * @param {Object} options
 * @param {string} options.boardId - The scenario board ID
 * @param {string[]} options.personIds - IDs of people to check
 * @param {string[]} [options.mainOnlyPersonIds] - IDs of main-only people selected to add; stored for resume, not conflict-checked
 * @param {string[]} options.fields - Field keys to check
 * @returns {Promise<Object|null>} - { processId } or null on error
 */
export const getScenarioDataRefreshConflicts = async ({
  boardId,
  personIds,
  mainOnlyPersonIds,
  fields
}) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${boardId}/scenario-data-refresh/conflicts`,
      { data: { personIds, mainOnlyPersonIds, fields } },
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (ret.status === 202) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.error(error.message)
    return null
  }
}

/**
 * Fetches a page of affected people for one conflicted field from a completed conflict check.
 *
 * @param {Object} options
 * @param {string} options.boardId - The scenario board ID
 * @param {string} options.processId - The conflict-check attempt's processId
 * @param {string} options.fieldKey - The field to fetch affected people for
 * @param {number} [options.offset] - Pagination offset
 * @param {number} [options.limit] - Page size
 * @returns {Promise<Object|null>} - { fieldKey, fieldLabel, affectedCount, people, hasMore } or null on error
 */
export const getScenarioDataRefreshConflictDetails = async ({
  boardId,
  processId,
  fieldKey,
  offset,
  limit
}) => {
  try {
    const ret = await axios.get(
      `${await API_URL()}/boards/${boardId}/scenario-data-refresh/conflicts/${processId}/details`,
      {
        params: { fieldKey, offset, limit },
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.error(error.message)
    return null
  }
}

/**
 * Executes a scenario data refresh: updates selected fields for selected people
 * in the scenario with current values from the main board, and optionally adds
 * main-only people to the scenario. When validateOnly is false, this only kicks off
 * the refresh — the backend runs it off-thread and returns 202 with a processId;
 * poll getScenarioRefreshAttempt()/use-refresh-job-status.js for the result.
 *
 * @param {Object} options
 * @param {string} options.boardId - The scenario board ID
 * @param {string[]} [options.personIds] - IDs of existing scenario people to refresh
 * @param {string[]} [options.fields] - Field keys to refresh for existing people
 * @param {Object} [options.excludeOverrides] - Per-field person exclusions { fieldKey: [personIds] }
 * @param {string[]} [options.mainOnlyPersonIds] - IDs of main-only people to add to the scenario
 * @param {boolean} [options.validateOnly] - When true, validates the refresh payload without applying changes
 * @returns {Promise<Object>} - { valid: true } for validateOnly, { processId, boardId } for a real
 *   kickoff, or { error }
 */
export const executeScenarioDataRefresh = async ({
  boardId,
  personIds,
  fields,
  excludeOverrides,
  mainOnlyPersonIds,
  validateOnly = false,
  backupName
}) => {
  try {
    const ret = await axios.post(
      `${await API_URL()}/boards/${boardId}/scenario-data-refresh`,
      {
        data: { personIds, fields, excludeOverrides, mainOnlyPersonIds, validateOnly, backupName }
      },
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (ret.status === 200 || ret.status === 202) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.error(error.message)
    return { error: error?.response?.data || error?.message || 'Scenario refresh failed' }
  }
}

/**
 * Fetch a scenario-data-refresh attempt's payload by processId.
 * @param {object} options
 * @param {string} options.boardId
 * @param {string} options.processId
 * @returns {Promise<object|null>} The attempt payload, or null if not found.
 */
export async function getScenarioRefreshAttempt({ boardId, processId }) {
  try {
    const ret = await axios.get(
      `${await API_URL()}/boards/${boardId}/refresh-attempt/${processId}`,
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    return ret.data
  } catch (error) {
    logger.info(error)
    return null
  }
}

/**
 * Discover the board's current unfinished scenario-data-refresh attempt, if any.
 * @param {object} options
 * @param {string} options.boardId
 * @returns {Promise<{processId: string}|null>} The active attempt, or null if none/on error.
 */
export async function getActiveScenarioRefreshAttempt({ boardId }) {
  try {
    const ret = await axios.get(`${await API_URL()}/boards/${boardId}/refresh-attempt`, {
      headers: { ...(await getAuthHeader()) }
    })

    return ret.data?.processId ? ret.data : null
  } catch (error) {
    logger.info(error)
    return null
  }
}
